import React, { useState } from 'react';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import SPADialog from '../../shared/SPADialog';
import MarkdownViewer from '../MarkdownViewer';

interface Props {
  contentType: 'lesson' | 'reference_material';
  rubric: string;
}

interface RubricInfo {
  name: string;
  system_prompt: string;
}

// AIDEV-NOTE: The rubric is a long markdown prompt, so a one-line "Loading…" made the dialog open
// short and jump as it arrived — and being transform-centred it moved vertically as it grew. Ragged
// line widths read as prose rather than as a table.
const SKELETON_WIDTHS = ['100%', '92%', '96%', '70%', '100%', '88%', '94%', '60%', '97%', '90%'];
const SKELETON_LINES = 28;

const RubricSkeleton = () => (
  <div className="tw-flex tw-flex-col tw-gap-2.5">
    {Array.from({ length: SKELETON_LINES }, (_, i) => (
      <div
        key={i}
        className="tw-h-4 tw-rounded tw-animate-pulse tw-bg-spa-bg-skeleton-subtle"
        style={{ width: SKELETON_WIDTHS[i % SKELETON_WIDTHS.length] }}
      />
    ))}
  </div>
);

// SPA fork of admin/ContentAudit/RubricDrawer, presented as an SPADialog. Fetches the exact
// rendered system prompt the gateway call uses, so the two can never drift.
const RubricDialog = ({ contentType, rubric }: Props) => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState<RubricInfo | null>(null);
  const [error, setError] = useState(false);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next || info) return;
    axios
      .get(Routes.plato_api_content_audit_rubric_path(), { params: { content_type: contentType, rubric } })
      .then(({ data }) => setInfo(data))
      .catch(() => setError(true));
  };

  return (
    <SPADialog open={open} onOpenChange={handleOpenChange}>
      <SPADialog.Trigger
        className={[
          'tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0',
          'tw-text-sm tw-font-bold tw-text-spa-accent-text',
          'hover:tw-text-spa-accent-text-hover hover:tw-underline',
        ].join(' ')}
      >
        View rubric
      </SPADialog.Trigger>
      <SPADialog.Content size="lg">
        <SPADialog.Header>
          <SPADialog.Title>
            {info?.name ?? 'Audit rubric'}
            {' — '}
            {contentType === 'reference_material' ? 'library material' : 'lesson'} variant
          </SPADialog.Title>
          <SPADialog.Description className="tw-mt-1 tw-text-spa-secondary">
            The exact instructions the AI reviewer runs with for this content type.
          </SPADialog.Description>
          <SPADialog.CloseButton />
        </SPADialog.Header>
        <SPADialog.Body>
          {error && (
            <p className="tw-m-0 tw-text-sm tw-text-spa-error">
              Couldn’t load the rubric. Close and try again.
            </p>
          )}
          {!info && !error && <RubricSkeleton />}
          {info && <MarkdownViewer content={info.system_prompt} />}
        </SPADialog.Body>
      </SPADialog.Content>
    </SPADialog>
  );
};

export default RubricDialog;
